/**
 * useNotificationSound — toca um som in-app ao chegar mensagem/alerta no chat.
 *
 * Spec: docs/superpowers/specs/2026-07-09-chat-notification-sound-design.md §3.
 *
 * Montado 1× no shell (AppLayout). Reusa a conexão SSE única via
 * `useNotificationsRealtime.onEvent` (NÃO abre conexão nova). Para cada evento:
 * monta o contexto (pref, usuário, mute), decide via `shouldPlay` (função pura)
 * e toca com debounce por kind. Toca mesmo com a conversa aberta/focada
 * (comportamento WhatsApp) — só própria/mute/preferência silenciam.
 */
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { ConversationListItem } from '@/api/chat'
import { useNotificationsRealtime, type RealtimeEvent } from './useNotificationsRealtime'
import { useAuthStore } from '@/store/auth'
import { playSound, getSoundEnabled, unlockAudio } from '@/lib/notificationSound'
import {
  shouldPlay,
  type SoundKind,
  type SoundDecisionEvent,
} from '@/lib/shouldPlayNotificationSound'

/** Debounce por kind: não metralhar em rajada de mensagens. */
const DEBOUNCE_MS = 2000

export function useNotificationSound(): void {
  const qc = useQueryClient()

  const lastPlayRef = useRef<Record<SoundKind, number>>({
    message: 0,
    alert_info: 0,
    alert_warning: 0,
    alert_critical: 0,
  })

  // Desbloqueia o áudio no 1º gesto do usuário (limitação de autoplay).
  useEffect(() => {
    unlockAudio()
  }, [])

  useNotificationsRealtime(
    {},
    {
      onEvent: (raw: RealtimeEvent & { conversation_id?: number; message?: SoundDecisionEvent['message'] }) => {
        const ev: SoundDecisionEvent = {
          type: raw.type,
          conversation_id: raw.conversation_id,
          message: raw.message,
        }

        const myUserId = useAuthStore.getState().user?.id ?? null

        // Mute por conversa: lê o flag is_muted do cache ['conversations']
        // (a lista dm/org agora traz o mute do caller). Best-effort: sem cache
        // ou conversa não encontrada → false (não silencia).
        const convs = qc.getQueryData<ConversationListItem[]>(['conversations'])
        const isConversationMuted =
          ev.conversation_id != null &&
          (convs?.find((c) => c.id === ev.conversation_id)?.is_muted ?? false)

        const kind = shouldPlay(ev, {
          myUserId,
          soundEnabled: getSoundEnabled(),
          isConversationMuted,
        })
        if (!kind) return

        // Debounce por kind.
        const now = Date.now()
        if (now - lastPlayRef.current[kind] < DEBOUNCE_MS) return
        lastPlayRef.current[kind] = now

        playSound(kind)
      },
    },
  )
}
